import dayjs from 'dayjs'

import { attachUploadedFiles } from '@/Ship/Helpers/attachUploadedFiles'

import { LETTER_ADDRESS_FIELDS } from '../Contracts/ILetter'
import { normalizeAddresses } from './letterAddresses'

import type { IFormEventContext } from '@/Core/Contracts/UI/Form/IFormConfig'

/** Тіло `PATCH /services/letters/{uuid}` (`SPEC-013` §7.8, `letter:update`). */
export interface LetterPatchBody {
    subject: string
    toWhom: string[]
    cc: string[]
    bcc: string[]
    body: string
    /** Нові вкладення кроку 1 — прикріплює бек (G-05) */
    fileUuids: string[]
}

/** Мінімум форми, з якого збирається тіло: значення й початкові значення вкладень. */
export interface LetterFormLike {
    values: Record<string, unknown>
    getInitialValue?: (path: string) => unknown
}

/**
 * Тіло збереження листа перед відправкою (`SPEC-013` §9.4):
 *
 *  - лише поля `letter:update` — статус, дату, власника й службове бек від
 *    форми не приймає;
 *  - адреси — три **незалежні** переліки (D-15), порожні рядки й повтори
 *    відкидаються, регістр — нижній;
 *  - вкладення — двокроково: щойно завантажені йдуть у `fileUuids`, уже
 *    прикріплені не надсилаються (`attachUploadedFiles`, вартовий
 *    завантажень). Видалене вкладення ядровий `FileInput` уже прибрав
 *    `DELETE /services/letter_files/{uuid}`.
 */
export const buildLetterPatchBody = (form: LetterFormLike): LetterPatchBody => {
    const values: Record<string, unknown> = { attachments: form.values['attachments'] }

    attachUploadedFiles('attachments')({
        values,
        formContext: { getInitialValue: (name: string) => form.getInitialValue?.(name) },
    } as unknown as IFormEventContext)

    const subject = form.values['subject']
    const body = form.values['body']
    const [toWhom, cc, bcc] = LETTER_ADDRESS_FIELDS.map((field) => normalizeAddresses(form.values[field]))

    return {
        subject: typeof subject === 'string' ? subject.trim() : '',
        toWhom: toWhom ?? [],
        cc: cc ?? [],
        bcc: bcc ?? [],
        body: typeof body === 'string' ? body : '',
        fileUuids: Array.isArray(values['fileUuids']) ? (values['fileUuids'] as string[]) : [],
    }
}

/**
 * Дата відкладеної відправки для `POST …/send` (`SPEC-013` §7.2): ISO 8601
 * зі зсувом браузера. У пояс інсталяції (Europe/Kyiv) її переводить бек.
 * Порожнє поле — негайна відправка; нерозібране значення — теж `null`, а не
 * «щось»: краще відправити зараз, ніж поставити в чергу на випадковий час.
 */
export const scheduledAtOf = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null

    const date = dayjs(value as string)

    return date.isValid() ? date.format() : null
}
